import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

const VALID_THEMES = ['neon-glow', 'gradient-arc', 'minimal', 'dot-ring', 'dot-ring-static', 'ambient'] as const;
type DialTheme = typeof VALID_THEMES[number];

export class RadialDialCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Theme patterns
    this.commandPatterns.set(
      /(?:set |change |use )?theme (?:to )?(.+)/i,
      (match) => {
        const theme = this.resolveTheme(match[1].trim());
        return theme ? { properties: { theme } } : null;
      }
    );

    this.commandPatterns.set(
      /(?:make (?:it )?|use )(neon[- ]?glow|gradient[- ]?arc|minimal|dot[- ]?ring|ambient)/i,
      (match) => {
        const theme = this.resolveTheme(match[1].trim());
        return theme ? { properties: { theme } } : null;
      }
    );

    // Range: "set range 0 to 255"
    this.commandPatterns.set(
      /(?:set )?range (\d+(?:\.\d+)?) (?:to|-) (\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { min: parseFloat(match[1]), max: parseFloat(match[2]) } })
    );

    // Min: "set min to 10", "min 0"
    this.commandPatterns.set(
      /(?:set )?min(?:imum)? (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { min: parseFloat(match[1]) } })
    );

    // Max: "set max to 100", "max 255"
    this.commandPatterns.set(
      /(?:set )?max(?:imum)? (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { max: parseFloat(match[1]) } })
    );

    // Step: "set step to 5", "step 0.1"
    this.commandPatterns.set(
      /(?:set )?step (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { step: parseFloat(match[1]) } })
    );

    // Unit: "set unit to %", "use degrees", "unit lux"
    this.commandPatterns.set(
      /(?:set )?unit (?:to )?["']?([^"']+)["']?/i,
      (match) => ({ properties: { unit: match[1].trim() } })
    );

    this.commandPatterns.set(
      /use (degrees|percent|%|celsius|fahrenheit|lux|psi|rpm|dB|watts?|volts?|amps?)/i,
      (match) => {
        const unitMap: Record<string, string> = {
          degrees: '°', percent: '%', '%': '%',
          celsius: '°C', fahrenheit: '°F',
          lux: 'lux', psi: 'PSI', rpm: 'RPM',
          db: 'dB', watt: 'W', watts: 'W',
          volt: 'V', volts: 'V', amp: 'A', amps: 'A',
        };
        const unit = unitMap[match[1].toLowerCase()] || match[1];
        return { properties: { unit } };
      }
    );

    // Value: "set value to 50", "value 75"
    this.commandPatterns.set(
      /(?:set )?value (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { value: parseFloat(match[1]) } })
    );

    // Decimals: "set decimals to 2"
    this.commandPatterns.set(
      /(?:set )?decimals? (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { decimals: parseInt(match[1], 10) } })
    );

    // Color (generic): "set color to red", "color blue"
    this.commandPatterns.set(
      /(?:set )?color (?:to )?(.+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { fillColor: color } } : null;
      }
    );

    // Fill color: "set fill color to orange", "fill orange"
    this.commandPatterns.set(
      /(?:set )?fill(?:\s+color)? (?:to )?(.+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { fillColor: color } } : null;
      }
    );

    // Track color: "track color dark gray"
    this.commandPatterns.set(
      /(?:set )?track(?:\s+color)? (?:to )?(.+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { trackColor: color } } : null;
      }
    );

    // Glow color: "glow blue", "set glow color to cyan"
    this.commandPatterns.set(
      /(?:set )?glow(?:\s+color)? (?:to )?(.+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { glowColor: color } } : null;
      }
    );

    // Glow intensity: "set glow intensity to 15"
    this.commandPatterns.set(
      /(?:set )?glow intensity (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { glowIntensity: parseFloat(match[1]) } })
    );

    // Knob color: "knob color red"
    this.commandPatterns.set(
      /(?:set )?knob(?:\s+color)? (?:to )?(.+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { knobColor: color } } : null;
      }
    );

    // Arc thickness: "set thickness to 12"
    this.commandPatterns.set(
      /(?:set )?(?:arc )?thickness (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { arcThickness: parseFloat(match[1]) } })
    );

    // Show/hide knob
    this.commandPatterns.set(
      /show knob/i,
      () => ({ properties: { showKnob: true } })
    );

    this.commandPatterns.set(
      /hide knob/i,
      () => ({ properties: { showKnob: false } })
    );

    // Arc angles: "set start angle to 120"
    this.commandPatterns.set(
      /(?:set )?(?:arc )?start ?angle (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { arcStartAngle: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set )?(?:arc )?end ?angle (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { arcEndAngle: parseFloat(match[1]) } })
    );
  }

  private resolveTheme(input: string): DialTheme | null {
    const normalized = input.toLowerCase().replace(/[\s_]+/g, '-');
    if ((VALID_THEMES as readonly string[]).includes(normalized)) {
      return normalized as DialTheme;
    }
    // Alias matching
    const aliases: Record<string, DialTheme> = {
      'neon': 'neon-glow',
      'glow': 'neon-glow',
      'gradient': 'gradient-arc',
      'arc': 'gradient-arc',
      'clean': 'minimal',
      'simple': 'minimal',
'dots': 'dot-ring',
      'dotring': 'dot-ring',
      'static dots': 'dot-ring-static',
      'static': 'dot-ring-static',
      'warm': 'ambient',
    };
    return aliases[normalized] || null;
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.RADIAL_DIAL && this.hasDialKeywords(command);
  }

  private hasDialKeywords(command: string): boolean {
    const keywords = [
      "dial", "radial", "dimmer", "rotary", "knob",
      "theme", "range", "min", "max", "step", "unit", "value", "decimals",
      "fill", "track", "glow", "knob color", "thickness", "color",
      "show knob", "hide knob",
      "start angle", "end angle",
      "neon-glow", "gradient-arc", "minimal", "dot-ring", "dot-ring-static", "ambient",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }
    }

    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the radial dial command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'Set theme to neon-glow',
      'Use ambient theme',
      'Make it minimal',
      'Set range 0 to 255',
      'Set min to 0',
      'Set max to 100',
      'Set step to 5',
      'Set value to 50',
      'Set unit to %',
      'Use degrees',
      'Set fill color to orange',
      'Glow blue',
      'Track color dark gray',
      'Set thickness to 12',
      'Show knob',
      'Hide knob',
      'Set glow intensity to 15',
      'Set start angle to 120',
      'Set decimals to 2',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = component.properties || {};

    // Theme suggestions
    const currentTheme = (props.theme as string) || 'neon-glow';
    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Set theme to ${otherThemes[0]}`);
    }

    // Range suggestions
    if (props.min === 0 && props.max === 100) {
      suggestions.push('Set range 0 to 255');
    }

    // Unit suggestions
    if (!props.unit || props.unit === '%') {
      suggestions.push('Set unit to °C', 'Use degrees');
    } else {
      suggestions.push('Set unit to %');
    }

    // Visual suggestions
    if (!props.fillColor) {
      suggestions.push('Set fill color to orange');
    }
    if (!props.glowColor) {
      suggestions.push('Glow blue');
    }

    // Arc suggestions
    suggestions.push('Set thickness to 12');

    // Knob suggestions
    if (props.showKnob !== false) {
      suggestions.push('Hide knob');
    } else {
      suggestions.push('Show knob');
    }

    return suggestions.slice(0, 12);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate theme
    if (changes.properties?.theme) {
      if (!(VALID_THEMES as readonly string[]).includes(changes.properties.theme)) {
        return { valid: false, error: `Invalid theme: "${changes.properties.theme}". Valid themes: ${VALID_THEMES.join(', ')}` };
      }
    }

    // Validate min < max
    const props = component.properties || {};
    const newMin = changes.properties?.min ?? props.min ?? 0;
    const newMax = changes.properties?.max ?? props.max ?? 100;
    if (newMin >= newMax) {
      return { valid: false, error: `Min (${newMin}) must be less than max (${newMax})` };
    }

    // Validate step is positive
    if (changes.properties?.step !== undefined && changes.properties.step <= 0) {
      return { valid: false, error: 'Step must be a positive number' };
    }

    // Validate arc thickness range
    if (changes.properties?.arcThickness !== undefined) {
      if (changes.properties.arcThickness < 1 || changes.properties.arcThickness > 50) {
        return { valid: false, error: 'Arc thickness must be between 1 and 50' };
      }
    }

    // Validate glow intensity range
    if (changes.properties?.glowIntensity !== undefined) {
      if (changes.properties.glowIntensity < 0 || changes.properties.glowIntensity > 30) {
        return { valid: false, error: 'Glow intensity must be between 0 and 30' };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const radialDialCommandHandler = new RadialDialCommandHandler();
